import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  User, Mail, Phone, MessageSquare, Paperclip, Send, CheckCircle,
  MapPin, Clock, ArrowLeft, X, ChevronRight, MessageCircle, Briefcase
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

const WorkWithUs = () => {
  const { t } = useTranslation()
  const [vagas, setVagas] = useState([])
  const [vagaSelecionada, setVagaSelecionada] = useState(null)
  const [vagaDetalhe, setVagaDetalhe] = useState(null)
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' })
  const [attachment, setAttachment] = useState(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    fetch(`${import.meta.env.BASE_URL}vagas.json`)
      .then((r) => r.json())
      .then(setVagas)
      .catch(() => setVagas([]))
  }, [])

  // Bloqueia scroll quando modal está aberto
  useEffect(() => {
    if (vagaDetalhe) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [vagaDetalhe])

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  const handleFileChange = (e) => setAttachment(e.target.files?.[0] || null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage('')
    try {
      const payload = new FormData()
      payload.append('name', formData.name)
      payload.append('email', formData.email)
      payload.append('phone', formData.phone)
      payload.append('vaga', vagaSelecionada ? vagaSelecionada.titulo : 'Candidatura Espontânea')
      payload.append('message', formData.message)
      if (attachment) payload.append('attachment', attachment)

      const response = await fetch('/api/work-with-us', { method: 'POST', body: payload })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.message || 'Falha ao enviar.')
      setIsSubmitted(true)
      setFormData({ name: '', email: '', phone: '', message: '' })
      setAttachment(null)
    } catch {
      const subject = `Candidatura${vagaSelecionada ? ` - ${vagaSelecionada.titulo}` : ' Espontânea'} - ${formData.name}`
      const body = [
        vagaSelecionada ? `Vaga de interesse: ${vagaSelecionada.titulo}` : 'Candidatura Espontânea',
        '',
        `Nome: ${formData.name}`,
        `Email: ${formData.email}`,
        `Telefone: ${formData.phone}`,
        '',
        'Mensagem:',
        formData.message,
      ].join('\n')
      window.location.href = `mailto:rh@agsgloballogistics.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
      setIsSubmitted(true)
    }
  }

  const inputClass = "w-full pl-12 pr-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition-all"
  const labelClass = "block text-sm font-semibold text-white/80 mb-2"

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#0C2D54' }}>
      <div className="container-custom py-16">

        <Link to="/contato" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition-colors mb-8">
          <ArrowLeft size={16} /> {t('forms.back')}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-10">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-3">
            {t('trabalheConosco.title')} <span style={{ color: '#38bdf8' }}>{t('trabalheConosco.titleHighlight')}</span>
          </h1>
          <p className="text-white/65 text-base">{t('trabalheConosco.subtitle')}</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* Coluna esquerda — Vagas */}
          {vagas.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:w-5/12 space-y-4"
            >
              <h2 className="text-lg font-bold text-white/90 mb-5 flex items-center gap-2">
                <Briefcase size={20} style={{ color: '#38bdf8' }} />
                {t('trabalheConosco.vagasAbertas')}
              </h2>

              {vagas.map((vaga) => {
                const ativa = vagaSelecionada?.id === vaga.id
                return (
                  <div
                    key={vaga.id}
                    className="rounded-2xl transition-all duration-200"
                    style={{
                      backgroundColor: ativa ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.07)',
                      border: ativa ? '1px solid rgba(56,189,248,0.5)' : '1px solid rgba(255,255,255,0.12)',
                    }}
                  >
                    {/* Cabeçalho clicável para selecionar */}
                    <button
                      type="button"
                      onClick={() => setVagaSelecionada(ativa ? null : vaga)}
                      className="w-full text-left p-5"
                    >
                      <h3 className="text-base font-bold text-white mb-2">{vaga.titulo}</h3>
                      <div className="flex flex-wrap gap-3 mb-2">
                        {vaga.local && (
                          <span className="flex items-center gap-1 text-xs text-white/55">
                            <MapPin size={12} /> {vaga.local}
                          </span>
                        )}
                        {vaga.tipo && (
                          <span className="flex items-center gap-1 text-xs text-white/55">
                            <Clock size={12} /> {vaga.tipo}
                          </span>
                        )}
                      </div>
                      {ativa && (
                        <p className="text-xs font-semibold mt-2" style={{ color: '#38bdf8' }}>✓ {t('trabalheConosco.vagaSelecionada')}</p>
                      )}
                    </button>

                    {/* Botão Ver detalhes */}
                    <div className="px-5 pb-4 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setVagaDetalhe(vaga)}
                        className="flex items-center gap-1.5 text-xs font-semibold transition-colors hover:opacity-80"
                        style={{ color: '#38bdf8' }}
                      >
                        <ChevronRight size={14} /> {t('trabalheConosco.verDescricao')}
                      </button>
                    </div>
                  </div>
                )
              })}

              <p className="text-xs text-white/40 pt-2">{t('trabalheConosco.naoPosicao')}</p>
            </motion.div>
          )}

          {/* Coluna direita — Formulário */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="flex-1"
          >
            <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>

              {vagaSelecionada ? (
                <div className="mb-6 px-4 py-3 rounded-xl text-sm font-semibold" style={{ backgroundColor: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8' }}>
                  {t('trabalheConosco.candidatando')} {vagaSelecionada.titulo}
                </div>
              ) : vagas.length > 0 ? (
                <div className="mb-6 px-4 py-3 rounded-xl text-sm text-white/50" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  {t('trabalheConosco.nenhuma')}
                </div>
              ) : null}

              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">{t('forms.applicationSent')}</h3>
                  <p className="text-white/65">{t('forms.applicationSentText')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t('forms.name')} {t('forms.required')}</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder={t('forms.namePlaceholder')} />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>{t('forms.email')} {t('forms.required')}</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder={t('forms.emailPlaceholder')} />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>{t('forms.phone')} {t('forms.required')}</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass} placeholder={t('forms.phonePlaceholder')} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>{t('forms.message')}</label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-4 text-white/40" size={20} />
                      <textarea name="message" value={formData.message} onChange={handleChange} rows="4" className={`${inputClass} resize-none`} placeholder="Conte um pouco sobre seu perfil e experiência..." />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>{t('forms.curriculum')}</label>
                    <label className="flex items-center justify-between gap-3 w-full px-4 py-3 rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)' }}>
                      <span className="text-white/55 text-sm truncate">{attachment ? attachment.name : t('forms.fileSelect')}</span>
                      <span className="flex items-center gap-1 text-sm font-semibold shrink-0" style={{ color: '#38bdf8' }}>
                        <Paperclip size={16} /> {t('forms.attach')}
                      </span>
                      <input type="file" name="attachment" onChange={handleFileChange} className="hidden" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" />
                    </label>
                  </div>

                  {errorMessage && (
                    <div className="rounded-lg p-4 text-sm text-red-300" style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                      {errorMessage}
                    </div>
                  )}

                  <button type="submit" className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:brightness-110" style={{ backgroundColor: '#38bdf8', color: '#0C2D54' }}>
                    <Send size={20} />
                    {t('forms.sendApplication')}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

        </div>
      </div>

      {/* ── MODAL DE DETALHES DA VAGA ───────────────────────────────────── */}
      <AnimatePresence>
        {vagaDetalhe && (
          <motion.div
            className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
            style={{ backgroundColor: 'rgba(5,18,45,0.85)', backdropFilter: 'blur(6px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVagaDetalhe(null)}
          >
            <motion.div
              className="relative w-full max-w-3xl rounded-2xl p-8 my-8 text-white"
              style={{ backgroundColor: '#0C2D54', border: '1px solid rgba(56,189,248,0.25)' }}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Fechar */}
              <button
                onClick={() => setVagaDetalhe(null)}
                className="absolute top-5 right-5 w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10 text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>

              {/* Título */}
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2 pr-10">{vagaDetalhe.titulo}</h2>

              {/* Tags */}
              <div className="flex flex-wrap gap-3 mb-6">
                {vagaDetalhe.local && (
                  <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(56,189,248,0.12)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.25)' }}>
                    <MapPin size={12} /> {vagaDetalhe.local}
                  </span>
                )}
                {vagaDetalhe.tipo && (
                  <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <Clock size={12} /> {vagaDetalhe.tipo}
                  </span>
                )}
                {vagaDetalhe.regime && (
                  <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    {vagaDetalhe.regime}
                  </span>
                )}
                {vagaDetalhe.locomocao && (
                  <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    {vagaDetalhe.locomocao}
                  </span>
                )}
              </div>

              {/* Benefícios */}
              {vagaDetalhe.beneficios && (
                <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.15)' }}>
                  <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#38bdf8' }}>{t('trabalheConosco.beneficios')}</p>
                  <p className="text-sm text-white/80">{vagaDetalhe.beneficios}</p>
                </div>
              )}

              {/* Descrição */}
              {vagaDetalhe.descricao && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#38bdf8' }}>{t('trabalheConosco.atividades')}</h3>
                  <p className="text-sm text-white/80 leading-relaxed">{vagaDetalhe.descricao}</p>
                </div>
              )}

              {/* Responsabilidades */}
              {vagaDetalhe.responsabilidades?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#38bdf8' }}>{t('trabalheConosco.responsabilidades')}</h3>
                  <ul className="space-y-2">
                    {vagaDetalhe.responsabilidades.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#38bdf8' }} />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Requisitos */}
              {vagaDetalhe.requisitos?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#38bdf8' }}>{t('trabalheConosco.requisitos')}</h3>
                  <ul className="space-y-2">
                    {vagaDetalhe.requisitos.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#38bdf8' }} />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Contato + botões */}
              {vagaDetalhe.contato && (
                <div className="pt-6 border-t border-white/10">
                  <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#38bdf8' }}>{t('trabalheConosco.interessados')}</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`mailto:${vagaDetalhe.contato.email}?subject=Candidatura - ${encodeURIComponent(vagaDetalhe.titulo)}`}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:brightness-110"
                      style={{ backgroundColor: '#38bdf8', color: '#0C2D54' }}
                    >
                      <Mail size={16} /> {vagaDetalhe.contato.email}
                    </a>
                    {vagaDetalhe.contato.whatsapp && (
                      <a
                        href={`https://wa.me/55${vagaDetalhe.contato.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:bg-white/10"
                        style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }}
                      >
                        <MessageCircle size={16} /> WhatsApp: {vagaDetalhe.contato.whatsapp}
                      </a>
                    )}
                  </div>

                  <button
                    onClick={() => { setVagaSelecionada(vagaDetalhe); setVagaDetalhe(null) }}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all hover:bg-white/10"
                    style={{ border: '1px solid rgba(56,189,248,0.4)', color: '#38bdf8' }}
                  >
                    {t('trabalheConosco.candidatar')}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default WorkWithUs
